import { BaseFeature } from '../../core/base-feature.js';

/**
 * JournalFeature - Frontend feature for managing mandatory journal system
 * Handles: prompts, entries, sessions, scheduling
 */
export class JournalFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.prompts = [];
        this.entries = [];
        this.sessionActive = false;
        this.currentSession = null;

        // Event callbacks for React components
        this.typingUpdateCallbacks = new Set();
        this.entrySubmittedCallbacks = new Set();
        this.sessionEndCallbacks = new Set();
        this.updateCallbacks = new Set();
        this.analysisCompleteCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'journal',
            name: 'Journal',
            version: 'v1.0',
            capabilities: [
                'manage_prompts',
                'view_entries',
                'control_sessions',
                'schedule_sessions'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Journal] Initializing...');
        console.log('[Journal] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId, fromId } = message;
        const command = cmd || action;

        switch (command) {
            // Real-time updates
            case 'typing_update':
                return this.handleTypingUpdate(payload);

            case 'entry_submitted':
                return this.handleEntrySubmitted(payload);

            case 'session_ended':
                return this.handleSessionEnded(payload);

            // Responses to commands
            case 'response':
                return this.handleGenericResponse(payload);

            case 'get_prompts_response':
                return this.handleResponse('get_prompts', payload);

            case 'add_prompt_response':
                return this.handleResponse('add_prompt', payload);

            case 'update_prompt_response':
                return this.handleResponse('update_prompt', payload);

            case 'delete_prompt_response':
                return this.handleResponse('delete_prompt', payload);

            case 'get_categories_response':
                return this.handleResponse('get_categories', payload);

            case 'get_entries_response':
                return this.handleResponse('get_entries', payload);

            case 'get_entry_response':
                return this.handleResponse('get_entry', payload);

            case 'start_session_response':
                return this.handleResponse('start_session', payload);

            case 'end_session_response':
                return this.handleResponse('end_session', payload);

            case 'get_session_state_response':
                return this.handleResponse('get_session_state', payload);

            case 'schedule_session_response':
                return this.handleResponse('schedule_session', payload);

            case 'get_schedule_response':
                return this.handleResponse('get_schedule', payload);

            case 'delete_schedule_response':
                return this.handleResponse('delete_schedule', payload);

            case 'get_stats_response':
                return this.handleResponse('get_stats', payload);

            case 'mark_entry_read_response':
                return this.handleResponse('mark_entry_read', payload);

            case 'mark_entries_read_response':
                return this.handleResponse('mark_entries_read', payload);

            case 'get_unread_count_response':
                return this.handleResponse('get_unread_count', payload);

            case 'get_unread_entries_response':
                return this.handleResponse('get_unread_entries', payload);

            case 'add_recurring_schedule_response':
                return this.handleResponse('add_recurring_schedule', payload);

            case 'get_recurring_schedules_response':
                return this.handleResponse('get_recurring_schedules', payload);

            case 'update_recurring_schedule_response':
                return this.handleResponse('update_recurring_schedule', payload);

            case 'delete_recurring_schedule_response':
                return this.handleResponse('delete_recurring_schedule', payload);

            case 'get_pending_sessions_response':
                return this.handleResponse('get_pending_sessions', payload);

            // New feature responses
            case 'add_tag_response':
                return this.handleResponse('add_tag', payload);
            case 'remove_tag_response':
                return this.handleResponse('remove_tag', payload);
            case 'get_tags_response':
                return this.handleResponse('get_tags', payload);
            case 'get_all_tags_response':
                return this.handleResponse('get_all_tags', payload);
            case 'get_entries_by_tag_response':
                return this.handleResponse('get_entries_by_tag', payload);
            case 'get_sentiment_response':
                return this.handleResponse('get_sentiment', payload);
            case 'get_sentiment_timeline_response':
                return this.handleResponse('get_sentiment_timeline', payload);
            case 'get_followups_response':
                return this.handleResponse('get_followups', payload);
            case 'get_links_response':
                return this.handleResponse('get_links', payload);
            case 'get_contradictions_response':
                return this.handleResponse('get_contradictions', payload);
            case 'get_active_contradictions_response':
                return this.handleResponse('get_active_contradictions', payload);
            case 'dismiss_contradiction_response':
                return this.handleResponse('dismiss_contradiction', payload);
            case 'add_invisible_requirement_response':
                return this.handleResponse('add_invisible_requirement', payload);
            case 'delete_invisible_requirement_response':
                return this.handleResponse('delete_invisible_requirement', payload);
            case 'get_invisible_requirements_response':
                return this.handleResponse('get_invisible_requirements', payload);

            case 'force_rewrite_response':
                return this.handleResponse('force_rewrite', payload);
            case 'get_rewrite_history_response':
                return this.handleResponse('get_rewrite_history', payload);
            case 'ghost_edit_entry_response':
                return this.handleResponse('ghost_edit_entry', payload);
            case 'delete_ghost_edit_response':
                return this.handleResponse('delete_ghost_edit', payload);
            case 'get_ghost_edit_response':
                return this.handleResponse('get_ghost_edit', payload);
            case 'force_contradiction_review_response':
                return this.handleResponse('force_contradiction_review', payload);
            case 'get_contradiction_review_response':
                return this.handleResponse('get_contradiction_review', payload);
            case 'enforce_followup_response':
                return this.handleResponse('enforce_followup', payload);
            case 'search_entries_response':
                return this.handleResponse('search_entries', payload);
            case 'rewrite_submitted':
                console.log('[Journal] Rewrite submitted:', payload);
                this.notifyUpdateListeners();
                return;
            case 'contradiction_reviewed':
                console.log('[Journal] Contradiction reviewed:', payload);
                this.notifyUpdateListeners();
                return;

            case 'analysis_complete':
                return this.handleAnalysisComplete(payload);

            case 'scheduled_pending':
                return this.handleScheduledPending(payload);

            default:
                console.warn(`[Journal] Unknown command: ${command}`);
        }
    }

    /**
     * Handle analysis complete event (from AI analyzer)
     */
    handleAnalysisComplete(payload) {
        console.log('[Journal] Analysis complete for entry:', payload.entryId);
        for (const callback of this.analysisCompleteCallbacks) {
            try { callback(payload); } catch (e) { console.error('[Journal] Analysis callback error:', e); }
        }
        this.notifyUpdateListeners();
    }

    /**
     * Handle scheduled session pending (user was idle)
     */
    handleScheduledPending(payload) {
        console.log('[Journal] Scheduled session pending:', payload);
        this.notifyUpdateListeners();
    }

    // =====================================
    // Real-time Event Handlers
    // =====================================

    handleTypingUpdate(payload) {
        this.notifyTypingUpdateListeners(payload);
    }

    handleEntrySubmitted(payload) {
        console.log('[Journal] Entry submitted:', payload.entryId);
        this.notifyEntrySubmittedListeners(payload);
    }

    handleSessionEnded(payload) {
        console.log('[Journal] Session ended:', payload.reason);
        this.sessionActive = false;
        this.currentSession = null;
        this.notifySessionEndListeners(payload);
    }

    // =====================================
    // Generic Response Handler
    // =====================================

    handleGenericResponse(payload) {
        const { requestId, ...data } = payload;
        if (requestId) {
            const resolve = this._pendingRequests.get(requestId);
            if (resolve) {
                this._pendingRequests.delete(requestId);
                resolve(data);
            }
        }
    }

    handleResponse(requestType, payload) {
        const resolve = this._pendingRequests.get(requestType);
        if (resolve) {
            this._pendingRequests.delete(requestType);
            resolve(payload);
        }
    }

    // =====================================
    // Prompt Methods
    // =====================================

    /**
     * Get all prompts
     * @param {Object} options - Query options
     * @returns {Promise<Object>} Prompts list
     */
    async getPrompts(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('get_prompts', options);
            this._pendingRequests.set('get_prompts', resolve);
        });
    }

    /**
     * Add a new prompt
     * @param {Object} promptData - Prompt data
     * @returns {Promise<Object>} Result
     */
    async addPrompt(promptData) {
        return new Promise((resolve) => {
            this.sendMessage('add_prompt', promptData);
            this._pendingRequests.set('add_prompt', resolve);
        });
    }

    /**
     * Update a prompt
     * @param {number} promptId - Prompt ID
     * @param {Object} updates - Update data
     * @returns {Promise<Object>} Result
     */
    async updatePrompt(promptId, updates) {
        return new Promise((resolve) => {
            this.sendMessage('update_prompt', { promptId, updates });
            this._pendingRequests.set('update_prompt', resolve);
        });
    }

    /**
     * Delete a prompt
     * @param {number} promptId - Prompt ID
     * @returns {Promise<Object>} Result
     */
    async deletePrompt(promptId) {
        return new Promise((resolve) => {
            this.sendMessage('delete_prompt', { promptId });
            this._pendingRequests.set('delete_prompt', resolve);
        });
    }

    /**
     * Get prompt categories
     * @returns {Promise<Object>} Categories list
     */
    async getCategories() {
        return new Promise((resolve) => {
            this.sendMessage('get_categories', {});
            this._pendingRequests.set('get_categories', resolve);
        });
    }

    // =====================================
    // Entry Methods
    // =====================================

    /**
     * Get journal entries
     * @param {Object} options - Query options
     * @returns {Promise<Object>} Entries list
     */
    async getEntries(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('get_entries', options);
            this._pendingRequests.set('get_entries', resolve);
        });
    }

    /**
     * Get a single entry
     * @param {number} entryId - Entry ID
     * @returns {Promise<Object>} Entry
     */
    async getEntry(entryId) {
        return new Promise((resolve) => {
            this.sendMessage('get_entry', { entryId });
            this._pendingRequests.set('get_entry', resolve);
        });
    }

    // =====================================
    // Session Methods
    // =====================================

    /**
     * Start a journal session
     * @param {number} promptId - Optional prompt ID (random if null)
     * @param {Object} settings - Session settings
     * @returns {Promise<Object>} Result
     */
    async startSession(promptId = null, settings = {}) {
        return new Promise((resolve) => {
            this.sendMessage('start_session', { promptId, settings });
            this._pendingRequests.set('start_session', resolve);
        });
    }

    /**
     * End the current session
     * @param {string} reason - End reason
     * @returns {Promise<Object>} Result
     */
    async endSession(reason = 'cancelled') {
        return new Promise((resolve) => {
            this.sendMessage('end_session', { reason });
            this._pendingRequests.set('end_session', resolve);
        });
    }

    /**
     * Get current session state
     * @returns {Promise<Object>} Session state
     */
    async getSessionState() {
        return new Promise((resolve) => {
            this.sendMessage('get_session_state', {});
            this._pendingRequests.set('get_session_state', resolve);
        });
    }

    // =====================================
    // Schedule Methods
    // =====================================

    /**
     * Schedule a journal session
     * @param {number} promptId - Prompt ID
     * @param {number} scheduledFor - Timestamp
     * @returns {Promise<Object>} Result
     */
    async scheduleSession(promptId, scheduledFor) {
        return new Promise((resolve) => {
            this.sendMessage('schedule_session', { promptId, scheduledFor });
            this._pendingRequests.set('schedule_session', resolve);
        });
    }

    /**
     * Get scheduled sessions
     * @param {Object} options - Query options
     * @returns {Promise<Object>} Schedule list
     */
    async getSchedule(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('get_schedule', options);
            this._pendingRequests.set('get_schedule', resolve);
        });
    }

    /**
     * Delete a scheduled session
     * @param {number} scheduleId - Schedule ID
     * @returns {Promise<Object>} Result
     */
    async deleteSchedule(scheduleId) {
        return new Promise((resolve) => {
            this.sendMessage('delete_schedule', { scheduleId });
            this._pendingRequests.set('delete_schedule', resolve);
        });
    }

    // =====================================
    // Stats
    // =====================================

    /**
     * Get journal statistics
     * @returns {Promise<Object>} Stats
     */
    async getStats() {
        return new Promise((resolve) => {
            this.sendMessage('get_stats', {});
            this._pendingRequests.set('get_stats', resolve);
        });
    }

    // =====================================
    // Read Status Methods
    // =====================================

    /**
     * Mark an entry as read
     * @param {number} entryId - Entry ID
     * @returns {Promise<Object>} Result
     */
    async markEntryRead(entryId) {
        return new Promise((resolve) => {
            this.sendMessage('mark_entry_read', { entryId });
            this._pendingRequests.set('mark_entry_read', resolve);
        });
    }

    /**
     * Mark multiple entries as read
     * @param {number[]} entryIds - Entry IDs
     * @returns {Promise<Object>} Result
     */
    async markEntriesRead(entryIds) {
        return new Promise((resolve) => {
            this.sendMessage('mark_entries_read', { entryIds });
            this._pendingRequests.set('mark_entries_read', resolve);
        });
    }

    /**
     * Get unread entry count
     * @returns {Promise<Object>} Count
     */
    async getUnreadCount() {
        return new Promise((resolve) => {
            this.sendMessage('get_unread_count', {});
            this._pendingRequests.set('get_unread_count', resolve);
        });
    }

    /**
     * Get unread entries
     * @param {number} limit - Max entries to return
     * @returns {Promise<Object>} Entries
     */
    async getUnreadEntries(limit = 50) {
        return new Promise((resolve) => {
            this.sendMessage('get_unread_entries', { limit });
            this._pendingRequests.set('get_unread_entries', resolve);
        });
    }

    // =====================================
    // Recurring Schedule Methods
    // =====================================

    /**
     * Add a recurring schedule
     * @param {Object} scheduleData - Schedule data
     * @returns {Promise<Object>} Result
     */
    async addRecurringSchedule(scheduleData) {
        return new Promise((resolve) => {
            this.sendMessage('add_recurring_schedule', scheduleData);
            this._pendingRequests.set('add_recurring_schedule', resolve);
        });
    }

    /**
     * Get recurring schedules
     * @param {boolean} activeOnly - Only active schedules
     * @returns {Promise<Object>} Schedules
     */
    async getRecurringSchedules(activeOnly = true) {
        return new Promise((resolve) => {
            this.sendMessage('get_recurring_schedules', { activeOnly });
            this._pendingRequests.set('get_recurring_schedules', resolve);
        });
    }

    /**
     * Update a recurring schedule
     * @param {number} scheduleId - Schedule ID
     * @param {Object} updates - Updates
     * @returns {Promise<Object>} Result
     */
    async updateRecurringSchedule(scheduleId, updates) {
        return new Promise((resolve) => {
            this.sendMessage('update_recurring_schedule', { scheduleId, updates });
            this._pendingRequests.set('update_recurring_schedule', resolve);
        });
    }

    /**
     * Delete a recurring schedule
     * @param {number} scheduleId - Schedule ID
     * @returns {Promise<Object>} Result
     */
    async deleteRecurringSchedule(scheduleId) {
        return new Promise((resolve) => {
            this.sendMessage('delete_recurring_schedule', { scheduleId });
            this._pendingRequests.set('delete_recurring_schedule', resolve);
        });
    }

    /**
     * Get pending sessions (queued for when user returns from idle)
     * @returns {Promise<Object>} Sessions
     */
    async getPendingSessions() {
        return new Promise((resolve) => {
            this.sendMessage('get_pending_sessions', {});
            this._pendingRequests.set('get_pending_sessions', resolve);
        });
    }

    // =====================================
    // Tag Methods
    // =====================================

    async addTag(entryId, tag, source = 'manual') {
        return new Promise((resolve) => {
            this.sendMessage('add_tag', { entryId, tag, source });
            this._pendingRequests.set('add_tag', resolve);
        });
    }

    async removeTag(entryId, tag) {
        return new Promise((resolve) => {
            this.sendMessage('remove_tag', { entryId, tag });
            this._pendingRequests.set('remove_tag', resolve);
        });
    }

    async getTags(entryId) {
        return new Promise((resolve) => {
            this.sendMessage('get_tags', { entryId });
            this._pendingRequests.set('get_tags', resolve);
        });
    }

    async getAllTags() {
        return new Promise((resolve) => {
            this.sendMessage('get_all_tags', {});
            this._pendingRequests.set('get_all_tags', resolve);
        });
    }

    async getEntriesByTag(tag, limit = 50) {
        return new Promise((resolve) => {
            this.sendMessage('get_entries_by_tag', { tag, limit });
            this._pendingRequests.set('get_entries_by_tag', resolve);
        });
    }

    // =====================================
    // Sentiment Methods
    // =====================================

    async getSentiment(entryId) {
        return new Promise((resolve) => {
            this.sendMessage('get_sentiment', { entryId });
            this._pendingRequests.set('get_sentiment', resolve);
        });
    }

    async getSentimentTimeline(limit = 100) {
        return new Promise((resolve) => {
            this.sendMessage('get_sentiment_timeline', { limit });
            this._pendingRequests.set('get_sentiment_timeline', resolve);
        });
    }

    // =====================================
    // Follow-up Methods
    // =====================================

    async getFollowups(entryId) {
        return new Promise((resolve) => {
            this.sendMessage('get_followups', { entryId });
            this._pendingRequests.set('get_followups', resolve);
        });
    }

    // =====================================
    // Link Methods
    // =====================================

    async getLinks(entryId) {
        return new Promise((resolve) => {
            this.sendMessage('get_links', { entryId });
            this._pendingRequests.set('get_links', resolve);
        });
    }

    // =====================================
    // Contradiction Methods
    // =====================================

    async getContradictions(entryId) {
        return new Promise((resolve) => {
            this.sendMessage('get_contradictions', { entryId });
            this._pendingRequests.set('get_contradictions', resolve);
        });
    }

    async getActiveContradictions(limit = 50) {
        return new Promise((resolve) => {
            this.sendMessage('get_active_contradictions', { limit });
            this._pendingRequests.set('get_active_contradictions', resolve);
        });
    }

    async dismissContradiction(contradictionId) {
        return new Promise((resolve) => {
            this.sendMessage('dismiss_contradiction', { contradictionId });
            this._pendingRequests.set('dismiss_contradiction', resolve);
        });
    }

    // =====================================
    // Invisible Requirement Methods
    // =====================================

    async addInvisibleRequirement(promptId, requirementType, requirementValue) {
        return new Promise((resolve) => {
            this.sendMessage('add_invisible_requirement', { promptId, requirementType, requirementValue });
            this._pendingRequests.set('add_invisible_requirement', resolve);
        });
    }

    async deleteInvisibleRequirement(requirementId) {
        return new Promise((resolve) => {
            this.sendMessage('delete_invisible_requirement', { requirementId });
            this._pendingRequests.set('delete_invisible_requirement', resolve);
        });
    }

    async getInvisibleRequirements() {
        return new Promise((resolve) => {
            this.sendMessage('get_invisible_requirements', {});
            this._pendingRequests.set('get_invisible_requirements', resolve);
        });
    }

    // =====================================
    // Force Rewrite Methods
    // =====================================

    async forceRewrite(entryId, reason) {
        return new Promise((resolve) => {
            this.sendMessage('force_rewrite', { entryId, reason });
            this._pendingRequests.set('force_rewrite', resolve);
        });
    }

    async getRewriteHistory(entryId) {
        return new Promise((resolve) => {
            this.sendMessage('get_rewrite_history', { entryId });
            this._pendingRequests.set('get_rewrite_history', resolve);
        });
    }

    // =====================================
    // Ghost Edit Methods
    // =====================================

    async ghostEditEntry(entryId, edits) {
        return new Promise((resolve) => {
            this.sendMessage('ghost_edit_entry', { entryId, edits });
            this._pendingRequests.set('ghost_edit_entry', resolve);
        });
    }

    async deleteGhostEdit(entryId) {
        return new Promise((resolve) => {
            this.sendMessage('delete_ghost_edit', { entryId });
            this._pendingRequests.set('delete_ghost_edit', resolve);
        });
    }

    async getGhostEdit(entryId) {
        return new Promise((resolve) => {
            this.sendMessage('get_ghost_edit', { entryId });
            this._pendingRequests.set('get_ghost_edit', resolve);
        });
    }

    // =====================================
    // Contradiction Review Methods
    // =====================================

    async forceContradictionReview(contradictionId) {
        return new Promise((resolve) => {
            this.sendMessage('force_contradiction_review', { contradictionId });
            this._pendingRequests.set('force_contradiction_review', resolve);
        });
    }

    async getContradictionReview(contradictionId) {
        return new Promise((resolve) => {
            this.sendMessage('get_contradiction_review', { contradictionId });
            this._pendingRequests.set('get_contradiction_review', resolve);
        });
    }

    // =====================================
    // Follow-up Enforcement
    // =====================================

    async enforceFollowup(followupId) {
        return new Promise((resolve) => {
            this.sendMessage('enforce_followup', { followupId });
            this._pendingRequests.set('enforce_followup', resolve);
        });
    }

    // =====================================
    // Search Methods
    // =====================================

    async searchEntries(query, limit = 20) {
        return new Promise((resolve) => {
            this.sendMessage('search_entries', { query, limit });
            this._pendingRequests.set('search_entries', resolve);
        });
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    /**
     * Subscribe to typing updates
     * @param {Function} callback - Called with typing data
     * @returns {Function} Unsubscribe function
     */
    onTypingUpdate(callback) {
        this.typingUpdateCallbacks.add(callback);
        return () => this.typingUpdateCallbacks.delete(callback);
    }

    /**
     * Subscribe to entry submitted events
     * @param {Function} callback - Called with entry data
     * @returns {Function} Unsubscribe function
     */
    onEntrySubmitted(callback) {
        this.entrySubmittedCallbacks.add(callback);
        return () => this.entrySubmittedCallbacks.delete(callback);
    }

    /**
     * Subscribe to session end events
     * @param {Function} callback - Called when session ends
     * @returns {Function} Unsubscribe function
     */
    onSessionEnd(callback) {
        this.sessionEndCallbacks.add(callback);
        return () => this.sessionEndCallbacks.delete(callback);
    }

    /**
     * Subscribe to general updates
     * @param {Function} callback - Called when data changes
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    /**
     * Subscribe to analysis complete events
     * @param {Function} callback - Called with analysis data
     * @returns {Function} Unsubscribe function
     */
    onAnalysisComplete(callback) {
        this.analysisCompleteCallbacks.add(callback);
        return () => this.analysisCompleteCallbacks.delete(callback);
    }

    notifyTypingUpdateListeners(data) {
        for (const callback of this.typingUpdateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Journal] Error in typing update callback:', error);
            }
        }
    }

    notifyEntrySubmittedListeners(data) {
        for (const callback of this.entrySubmittedCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Journal] Error in entry submitted callback:', error);
            }
        }
    }

    notifySessionEndListeners(data) {
        for (const callback of this.sessionEndCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Journal] Error in session end callback:', error);
            }
        }
    }

    notifyUpdateListeners() {
        const data = {
            prompts: this.prompts,
            entries: this.entries,
            sessionActive: this.sessionActive,
            currentSession: this.currentSession
        };
        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Journal] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getPromptsList() {
        return this.prompts;
    }

    getEntriesList() {
        return this.entries;
    }

    isSessionActive() {
        return this.sessionActive;
    }

    getCurrentSession() {
        return this.currentSession;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[Journal] Cleaning up...');
        this.prompts = [];
        this.entries = [];
        this.sessionActive = false;
        this.currentSession = null;
        this.typingUpdateCallbacks.clear();
        this.entrySubmittedCallbacks.clear();
        this.sessionEndCallbacks.clear();
        this.updateCallbacks.clear();
        this.analysisCompleteCallbacks.clear();
        this._pendingRequests.clear();
    }
}
